// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { difference, intersection, xor } from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as Immutable from 'immutable';
import memoizeOne from 'memoize-one';

// Utils
import * as rectLib from '../../../common/maths/geometry/rect';
import { asObject, toIdArray } from '../../../common/utils/immutableHelper';
import rafThrottle from '../../../common/utils/lib/rafThrottle';
import { getDomElementId } from '../../element/utils/elementUtil';
import { getScrollAsPoint } from '../../utils/dom/scrollableParentUtils';
import getParentScrollableSectionForElementId from '../../utils/dom/getParentScrollableSectionForElementId';
import { getBezierIntersection, getBezierObject } from '../../../common/maths/geometry/bezierUtil';
import { convertClientRectToUnscaledSectionCoordinates } from '../utils/canvasPositionUtils';

// Actions
import {
    addSelectedElements,
    setSelectedElements,
    deselectAllElements,
    removeSelectedElements,
} from '../../element/selection/selectionActions';

// Selectors
import { getSelectedElementIds } from '../../element/selection/selectedElementsSelector';
import { getClipboardCutElementsIdMapSelector } from '../../workspace/shortcuts/clipboard/clipboardSelectors';
import { getElements } from '../../element/selectors/elementsSelector';

// Constants
import { LINE_SVG_HEIGHT, LINE_SVG_WIDTH } from '../../element/line/lineUiConstants';

/**
 * Gets the bezier control points based on the line's SVG path.
 * This also translates the lines into the document's coordinate system by translating into
 * the line element's top left point, which is "0,0" for the line path's coordinates.
 */
const parsePathIntoBezierPoints = (path, lineElementRect) => {
    if (!path) return null;

    const components = path.split(' ');

    if (components.length !== 7) return null;

    const start = {
        x: parseInt(components[0].substring(1), 10) + lineElementRect.left,
        y: parseInt(components[1], 10) + lineElementRect.top,
    };

    // 2 is "Q"
    const controlPoint = {
        x: parseInt(components[3], 10) + lineElementRect.left,
        y: parseInt(components[4], 10) + lineElementRect.top,
    };

    const end = {
        x: parseInt(components[5], 10) + lineElementRect.left,
        y: parseInt(components[6], 10) + lineElementRect.top,
    };

    return {
        start,
        controlPoint,
        end,
    };
};

/**
 * This creates a Map of element IDs to functions that accept a marquee rectangle and return whether
 * this element intersects the rectangle, or is contained by it.
 * This is memoised so it only needs to calculate whenever the measurementsMap changes.
 *
 * NOTE: This could potentially accept the elements map as well, if we needed it, but for now I'm
 * using a hack to identify all lines as having a width and height of 10px (as I've set this on the
 * LineSvg element). I think this should work fine, as no other element should ever be 10px x 10px,
 * but if the size of the LineSvg changes, or this assumption proves to be incorrect, revisit this logic.
 */
const getElementIntersectionFunctions = memoizeOne(
    (
        sectionElementIds,
        zoomScale = 1,
        zoomTranslationPx = { x: 0, y: 0 },

        // The elements map and cutElementIdsMap isn't used in the resulting functions, it's only purpose is to correctly
        // memoise the function. Lines might change their control point, but it won't change the measurements map.
        elements,
        cutElementIdsMap,
    ) => {
        let sectionNode = null;
        const zoomTranslationPxObject = asObject(zoomTranslationPx);

        return sectionElementIds.reduce((acc, elementId) => {
            const domElementId = getDomElementId(elementId);
            const domElement = document.getElementById(domElementId);

            sectionNode = sectionNode || getParentScrollableSectionForElementId(elementId);

            if (!domElement || !sectionNode) return acc;

            // Get the bounding rect from the top left corner of the browser viewport
            const elementClientRect = domElement.getBoundingClientRect();

            const sectionClientRect = sectionNode.getBoundingClientRect();
            const sectionScroll = getScrollAsPoint(sectionNode);

            const elementRect = convertClientRectToUnscaledSectionCoordinates(
                elementClientRect,
                sectionClientRect,
                sectionScroll,
                zoomScale,
                zoomTranslationPxObject,
            );

            const { width, height } = elementRect;

            // Slight hack - If the width and height equal 10, then it's a line, so handle them differently
            if (Math.round(width) === LINE_SVG_WIDTH && Math.round(height) === LINE_SVG_HEIGHT) {
                // Otherwise - we want to get the actual line element and its path, so we can figure out
                // if the bezier curve intersects
                const domId = getDomElementId(elementId);
                const lineElementDomNode = document.getElementById(domId);
                const linePathDomElement = lineElementDomNode
                    ? lineElementDomNode.querySelector('.svg-line .line-path')
                    : null;

                if (linePathDomElement) {
                    // Line element rect - use this for translation of the bezier points into the canvas space
                    const lineElementBoundingBox = lineElementDomNode.getBoundingClientRect();
                    const lineElementRect = convertClientRectToUnscaledSectionCoordinates(
                        lineElementBoundingBox,
                        sectionClientRect,
                        sectionScroll,
                        zoomScale,
                        zoomTranslationPxObject,
                    );

                    // Get the line element's bounding box
                    const linePathBoundingBox = linePathDomElement.getBoundingClientRect();
                    const lineRect = convertClientRectToUnscaledSectionCoordinates(
                        linePathBoundingBox,
                        sectionClientRect,
                        sectionScroll,
                        zoomScale,
                        zoomTranslationPxObject,
                    );

                    // Get the bezier points off the line's element's path
                    const path = linePathDomElement.getAttribute('d');
                    // Translate the points based on the line element's starting position
                    const bezierPoints = parsePathIntoBezierPoints(path, lineElementRect);

                    // Convert the points into bezier object
                    const bezier = bezierPoints
                        ? getBezierObject(bezierPoints.start, bezierPoints.controlPoint, bezierPoints.end)
                        : null;

                    return acc.set(elementId, (marqueeRect) => {
                        // if the marquee completely contains the line rect, then the line is selected
                        if (rectLib.containsRect(marqueeRect, lineRect)) return true;

                        if (!rectLib.isOverlapping(lineRect, marqueeRect)) return false;

                        // This is more expensive than the previous checks, so only perform it if we need to
                        return !!getBezierIntersection(marqueeRect, bezier);
                    });
                }
            }

            // Normal elements are rectangular, so we can simply use the rectangle isOverlapping method
            return acc.set(elementId, (marqueeRect) => rectLib.isOverlapping(elementRect, marqueeRect));
        }, Immutable.Map());
    },
);

/**
 * Builds a map of functions that will check if the marquee intersects or contains the element.
 */
const getElementIntersectionFunctionsThunk =
    (sectionElementIds, zoomScale, zoomTranslationPx) => (dispatch, getState) => {
        const state = getState();
        const elements = getElements(state);
        const cutElementIdsMap = getClipboardCutElementsIdMapSelector(state);
        return getElementIntersectionFunctions(
            sectionElementIds,
            zoomScale,
            zoomTranslationPx,
            elements,
            cutElementIdsMap,
        );
    };

const mapStateToProps = createStructuredSelector({
    currentlySelectedElementIds: getSelectedElementIds,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSetSelectedElements: (ids) => dispatch(setSelectedElements({ ids })),
    dispatchAddSelectedElements: (ids) => dispatch(addSelectedElements({ ids })),
    dispatchRemoveSelectedElements: (ids) => dispatch(removeSelectedElements({ ids })),
    dispatchDeselectAllElements: (ids) => dispatch(deselectAllElements({ ids })),
    dispatchGetElementIntersectionCheckFns: (sectionElementIds, zoomScale, zoomTranslationPx) =>
        dispatch(getElementIntersectionFunctionsThunk(sectionElementIds, zoomScale, zoomTranslationPx)),
});

const MARQUEE_SELECTION_CLASS = 'marquee-selected';
const MARQUEE_REMOVE_SELECTION_CLASS = 'marquee-remove-selected';
/**
 * PERFORMANCE:
 * The following functions use vanilla JS to add classes to hovered elements for performance reasons.
 * 1. This prevents dispatching store state changes every time an element is hovered (thus preventing many re-renders
 *      or prop recalculations).
 * 2. Actually selecting elements causes their order in the DOM to change (due to the sorting of elements on the
 *      canvas), thus selecting and deselecting elements causes reasonably significant DOM changes in a small amount
 *      of time.  By using classes instead minimal DOM changes are required and instead of relying on React to
 *      perform reconciliation the browser only has to perform style & paint recalculations.
 */
const modifySelectionMarqueeClass = (modificationType, className) => (elementIds) => {
    elementIds.forEach((id) => {
        const domElement = document.getElementById(getDomElementId(id));
        if (!domElement) return;

        domElement.classList[modificationType](className);
    });
};

const addSelectionMarqueeHoveredClass = modifySelectionMarqueeClass('add', MARQUEE_SELECTION_CLASS);
const removeSelectionMarqueeHoveredClass = modifySelectionMarqueeClass('remove', MARQUEE_SELECTION_CLASS);

const addSelectionMarqueeRemoveSelectionClass = modifySelectionMarqueeClass('add', MARQUEE_REMOVE_SELECTION_CLASS);
const removeSelectionMarqueeRemoveSelectionClass = modifySelectionMarqueeClass(
    'remove',
    MARQUEE_REMOVE_SELECTION_CLASS,
);

/**
 * Cleanup the marquee classes when we're finished.
 */
const removeMarqueeSelectionClasses = () => {
    const marqueeSelectedNodes = document.querySelectorAll(`.${MARQUEE_SELECTION_CLASS}`);
    Array.from(marqueeSelectedNodes).forEach((domElement) => {
        domElement && domElement.classList.remove(MARQUEE_SELECTION_CLASS);
    });

    const marqueeSelectionRemovedNodes = document.querySelectorAll(`.${MARQUEE_REMOVE_SELECTION_CLASS}`);
    Array.from(marqueeSelectionRemovedNodes).forEach((domElement) => {
        domElement && domElement.classList.remove(MARQUEE_REMOVE_SELECTION_CLASS);
    });
};

export default (DecoratedComponent) => {
    @connect(mapStateToProps, mapDispatchToProps)
    class SelectionMarqueeSelectedElementFinderDecorator extends React.Component {
        constructor(props) {
            super(props);
            this.hoveredElementIds = [];
        }

        componentWillReceiveProps(nextProps) {
            if (nextProps.active) {
                this.throttledFindHoveredElements(nextProps);
            }
        }

        componentWillUnmount() {
            this.throttledFindHoveredElements.cancel();
        }

        findHoveredElements = ({ canvasDocumentMarqueeRect, dispatchGetElementIntersectionCheckFns }) => {
            const { sectionElementIds, active, appendMode, currentlySelectedElementIds, zoomScale, zoomTranslationPx } =
                this.props;

            if (!active || !canvasDocumentMarqueeRect.width || !canvasDocumentMarqueeRect.height) return;

            // Element Intersection
            const elementIntersectionCheckFunctions = dispatchGetElementIntersectionCheckFns(
                sectionElementIds,
                zoomScale,
                zoomTranslationPx,
            );

            const currentHoveredElements = elementIntersectionCheckFunctions.filter((fn) =>
                fn(canvasDocumentMarqueeRect),
            );

            const currentHoveredElementIds = toIdArray(currentHoveredElements);
            const currentHoveredSectionElementIds = intersection(sectionElementIds, currentHoveredElementIds);

            const currentlySelectedElementIdsArray = asObject(currentlySelectedElementIds);

            const previouslyHovered = this.hoveredElementIds;
            this.hoveredElementIds = appendMode
                ? xor(currentlySelectedElementIdsArray, currentHoveredSectionElementIds)
                : currentHoveredSectionElementIds;

            const noLongerHovered = difference(previouslyHovered, this.hoveredElementIds);
            const newHovered = difference(this.hoveredElementIds, previouslyHovered);

            const newRemoved = appendMode
                ? intersection(currentHoveredSectionElementIds, currentlySelectedElementIdsArray)
                : [];
            const noLongerRemoved = appendMode
                ? difference(currentlySelectedElementIdsArray, currentHoveredSectionElementIds)
                : [];

            newHovered.length && addSelectionMarqueeHoveredClass(newHovered);
            noLongerHovered.length && removeSelectionMarqueeHoveredClass(noLongerHovered);

            newRemoved.length && addSelectionMarqueeRemoveSelectionClass(newRemoved);
            noLongerRemoved.length && removeSelectionMarqueeRemoveSelectionClass(noLongerRemoved);
        };

        throttledFindHoveredElements = rafThrottle(this.findHoveredElements);

        startMarqueeSelect = (args) => {
            const { appendMode } = args;
            const { dispatchDeselectAllElements, startMarqueeSelect } = this.props;

            if (!appendMode) {
                dispatchDeselectAllElements();
            }

            return startMarqueeSelect(args);
        };

        finishMarqueeSelect = (...args) => {
            const { finishMarqueeSelect, dispatchSetSelectedElements } = this.props;

            this.throttledFindHoveredElements.cancel();

            dispatchSetSelectedElements(this.hoveredElementIds);
            removeMarqueeSelectionClasses();

            this.hoveredElementIds = [];
            return finishMarqueeSelect(...args);
        };

        render() {
            return (
                <DecoratedComponent
                    {...this.props}
                    finishMarqueeSelect={this.finishMarqueeSelect}
                    startMarqueeSelect={this.startMarqueeSelect}
                />
            );
        }
    }

    SelectionMarqueeSelectedElementFinderDecorator.propTypes = {
        sectionElementIds: PropTypes.array.isRequired,
        currentlySelectedElementIds: PropTypes.object,
        dispatchSetSelectedElements: PropTypes.func,
        dispatchAddSelectedElements: PropTypes.func,
        dispatchRemoveSelectedElements: PropTypes.func,
        dispatchDeselectAllElements: PropTypes.func,
        startMarqueeSelect: PropTypes.func,
        finishMarqueeSelect: PropTypes.func,
        dispatchGetElementIntersectionCheckFns: PropTypes.func,
        active: PropTypes.bool,
        appendMode: PropTypes.bool,
        top: PropTypes.number,
        left: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
        zoomScale: PropTypes.number,
        zoomTranslationPx: PropTypes.object,
    };

    return SelectionMarqueeSelectedElementFinderDecorator;
};
